import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import "./ProposalNavCount.css";

export type ProposalNavCounts = {
  total: number;
  bySchool: Record<string, number>;
};

const EMPTY: ProposalNavCounts = { total: 0, bySchool: {} };

let current: ProposalNavCounts = EMPTY;
const listeners = new Set<() => void>();

function publish(next: ProposalNavCounts) {
  current = next;
  listeners.forEach((fn) => fn());
}

/** Reload the Courses nav count. Safe to call after Apply or Reject. */
export function refreshProposalNavCounts() {
  return api<{ success: boolean; data?: ProposalNavCounts }>("/api/courses/proposal-counts")
    .then((envelope) => {
      const data = envelope.data;
      publish({
        total: Number(data?.total) || 0,
        bySchool: data?.bySchool && typeof data.bySchool === "object" ? data.bySchool : {},
      });
    })
    .catch(() => {
      publish(EMPTY);
    });
}

/** Pending course requests this seat can apply. Stays at zero when Courses is not in the nav. */
export function useProposalNavCounts(enabled: boolean, refreshKey = "") {
  const [value, setValue] = useState(current);
  useEffect(() => {
    if (!enabled) {
      setValue(EMPTY);
      return;
    }
    const follow = () => setValue(current);
    listeners.add(follow);
    void refreshProposalNavCounts();
    return () => {
      listeners.delete(follow);
    };
  }, [enabled, refreshKey]);
  return enabled ? value : EMPTY;
}

/** The number on Courses, or on a school row. Renders nothing at zero. */
export default function ProposalNavCount({
  count,
  variant = "inline",
}: {
  count: number;
  variant?: "inline" | "icon";
}) {
  if (!count) return null;
  const label = count === 1 ? "1 course request waiting" : `${count} course requests waiting`;
  return (
    <span className={`al-proposal-nav-count al-proposal-nav-count--${variant}`} title={label}>
      {count}
    </span>
  );
}
